/* The vocabulary of the pipeline, in one place.
 *
 * The board and the record both name stages, and in the shipped demo they named them
 * differently: the column head said "Trial 1 · done" and the badge on the record said
 * "Prospect 1 · Trial done". Both spellings are kept, because they are answering
 * different questions - a column head has five across at 1440 and a badge has a whole
 * line - but they are kept TOGETHER, so a stage cannot be renamed in one and not the
 * other.
 *
 * SOURCE AND HEARD ARE DIFFERENT QUESTIONS and are never collapsed. Somebody a member
 * referred who then filled in the web form is source Website, heard "Friend or
 * member"; merging them loses the referral, which is the single most valuable thing
 * on the form.
 */
import type { Lead, LeadStage } from '@acrostic/model';

export interface StageMeta {
  /** What a column head says. Fits five across at 1440 on one line. */
  short: string;
  /** What a badge on the record says. */
  long: string;
  tone: 'info' | 'warn' | 'plain' | 'good' | 'risk';
}

export const STAGES: Record<LeadStage, StageMeta> = {
  new:        { short: 'New',               long: 'New Lead',                    tone: 'info' },
  contacted:  { short: 'Contacted',         long: 'Contacted',                   tone: 'info' },
  prospect_1: { short: 'Trial 1 · done',    long: 'Prospect 1 · Trial done',     tone: 'warn' },
  prospect_2: { short: 'Trial 2 · both done', long: 'Prospect 2 · Both trials',  tone: 'warn' },
  nurture:    { short: 'Nurture',           long: 'Nurture · Unconverted',       tone: 'plain' },
  won:        { short: 'Member',            long: 'Member',                      tone: 'good' },
  lost:       { short: 'Lost',              long: 'Lost',                        tone: 'risk' },
};

/** The five columns. Won and lost are history: see matchLeadScope in SeedSource. */
export const OPEN_PATH: LeadStage[] = ['new', 'contacted', 'prospect_1', 'prospect_2', 'nurture'];

export const SOURCE_UNRECORDED = 'Not recorded';

/** The category plus what drove them to it: "Funnel · Meta Ads". */
export function sourceText(l: Lead): string {
  if (!l.source) return SOURCE_UNRECORDED;
  return l.sourceDetail ? `${l.source} · ${l.sourceDetail}` : l.source;
}

/* WHO THIS LEAD IS TO CALL, and it is not the same question as who knows them.
 *
 * `owner` is the desk's ROUTING default: a new enquiry is put on somebody's list
 * before anybody has spoken to them. The ANCHOR is the person who has actually had a
 * two-way conversation (SITE-RULES 2), and a form submission is not a relationship.
 *
 * The board groups and filters on the routing, because "whose call is this" is what a
 * board is for. The record shows the anchor, because "who knows them" is what a record
 * is for. They are different fields answering different questions and the shipped demo
 * got this wrong once already: the Ownership card named a support coach while the panel
 * beside it said, correctly, that nobody owned them yet. */
export function routedTo(l: Lead): string | null {
  return l.owner ?? null;
}

/** The person who has actually spoken to them, or nobody. */
export function anchorOf(l: Lead): string | null {
  return l.anchor?.name ?? null;
}

/* ── The enquiry form's vocabulary ───────────────────────────────────────────
   Three questions that are NOT the same question, and all three are asked:

     source      the MECHANISM they arrived through (walk-in, funnel, website)
     subcategory what drove them to it (a Meta ad, a Yelp listing, a named event)
     heard       what made them aware of the gym at ALL (a member told them)

   Somebody a member referred who then filled in the web form is source Website,
   heard "Friend or member". Collapsing those loses the referral, which is the single
   most valuable answer on the form. The migration's "Others" bucket is 185 answers
   that were thrown away exactly this way. */

export const SOURCE_CATEGORIES = ['Website', 'Funnel', 'Walk-in', 'Event', 'Class Pass', 'Phone'] as const;

export const SOURCE_SUBS: Record<string, readonly string[]> = {
  Funnel: ['Meta Ads', 'Google Search Ads', 'Instagram'],
  Website: ['Yelp'],
  Phone: ['Yelp'],
  Event: [
    'Saturday Open House', 'Spring 5K Expo', 'Downtown Farmers Market',
    'Summer Strong Launch Night', 'Charity Bootcamp', 'Willow Glen Wellness Fair',
    'Bring a Friend Saturday',
  ],
};

export const HEARD_OPTIONS = [
  'Friend or member', 'Google Search', 'Instagram', 'Facebook', 'Website',
  'YELP', 'Walked or drove past', 'Event', 'ClassPass app',
] as const;

/* Not an "Other" bucket. Picking this asks for the actual answer and keeps it. */
export const HEARD_FREETEXT = '__freetext__';
